import { useEffect, useRef } from 'react';

/**
 * useVideoDurationFix
 *
 * WebM blob'larında `video.duration === Infinity` olan klasik MediaRecorder
 * sorununu giderir. Tarayıcıyı metadata'yı yeniden okumaya zorlamak için
 * `currentTime = 1e101` setleme trick'ini kullanır; gerçek süre gelince
 * `currentTime = 0`'a geri döner.
 *
 * Sadece blob URL / src ile yüklenen playback videolarına uygulanmalıdır.
 * srcObject (live preview) ile kullanılmaz — blobUrl olmadığında hook
 * hiçbir şey yapmaz.
 *
 * Sağlamlaştırma notları:
 *  - videoRef dependency listesinden çıkarıldı (ref kimliği değişmez), sadece [blobUrl].
 *  - effect bağlanırken video zaten HAVE_METADATA durumundaysa (readyState >= 1)
 *    loadedmetadata olayı kaçırılmış demektir; onLoadedMetadata mantığı elle çağrılır.
 *  - onTimeUpdate → onDurationResolved olarak yeniden adlandırıldı (okunabilirlik).
 *  - Guard (appliedRef) blobUrl değişiminde effect re-run'da otomatik sıfırlanır.
 *
 * @param {React.RefObject<HTMLVideoElement>} videoRef - playback <video> ref
 * @param {string|null} blobUrl - video src (blob: veya http/https URL)
 */
export default function useVideoDurationFix(videoRef, blobUrl) {
  // Guard: bir kez sarar, tekrar tetiklenmez
  const appliedRef = useRef(false);

  useEffect(() => {
    // blobUrl yoksa (live preview) ya da ref yoksa dokunma
    if (!blobUrl || !videoRef) return;

    const video = videoRef.current;
    if (!video) return;

    // Yeni bir blobUrl geldiğinde guard sıfırla
    appliedRef.current = false;

    function onLoadedMetadata() {
      if (appliedRef.current) return;
      if (video.duration === Infinity || isNaN(video.duration)) {
        // Tarayıcıyı metadata sürümünü yeniden hesaplamaya zorla
        video.currentTime = 1e101;
      }
    }

    // onDurationResolved: gerçek süre gelince başa sar ve guard'ı kapat.
    // Hem timeupdate hem durationchange dinleniyor — hangisi önce gelirse.
    function onDurationResolved() {
      if (appliedRef.current) return;
      if (video.duration !== Infinity && !isNaN(video.duration) && video.duration > 0) {
        // Gerçek süre geldi — başa sar, guard'ı kapat
        appliedRef.current = true;
        video.currentTime = 0;
      }
    }

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('timeupdate', onDurationResolved);
    video.addEventListener('durationchange', onDurationResolved);

    // Sağlamlaştırma: effect bağlanırken video zaten HAVE_METADATA durumundaysa
    // (readyState >= 1) 'loadedmetadata' olayı çoktan ateşlenmiş; elle çağır.
    if (video.readyState >= 1) {
      onLoadedMetadata();
    }

    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('timeupdate', onDurationResolved);
      video.removeEventListener('durationchange', onDurationResolved);
    };
    // videoRef dependency'sini kasıtlı olarak çıkardık:
    // ref kimliği React döngüsü boyunca sabittir; blobUrl değişimi yeterli.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blobUrl]);
}
